import { useI18nStore } from "@/stores/i18nStore";
import {
  formatChartDate,
  formatTooltipTimestamp,
} from "@/utils/dateLocalization";
import React from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface EmotionalWellbeingChartProps {
  data: Array<{
    date: string;
    emotionalWellbeing?: number;
    emotionalWellness?: number;
    timestamp: string;
    formattedDate: string;
  }>;
  className?: string;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: {
      formattedDate?: string;
      timestamp?: string;
      fhirTimestamp?: string;
      emotionalWellbeing?: number;
      emotionalWellness?: number;
    };
  }>;
}

const CustomTooltip: React.FC<TooltipProps> = ({ active, payload }) => {
  const { t } = useI18nStore();

  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    if (!data) return null;

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="text-sm font-medium text-gray-900">
          {data.formattedDate || ""}
        </p>
        <p className="text-xs text-gray-500 mb-2">
          {t.recorded}: {data.fhirTimestamp || data.timestamp || ""}
        </p>
        <div className="mt-2 space-y-1">
          {(data.emotionalWellbeing !== undefined ||
            data.emotionalWellness !== undefined) && (
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <span className="text-sm text-gray-600">
                😊 {t.emotionalWellbeing}:{" "}
                {data.emotionalWellbeing || data.emotionalWellness || 0}/5
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

// Custom Dot Component with Smileys
const CustomDot = (props: {
  cx?: number;
  cy?: number;
  payload?: {
    emotionalWellness?: number;
  };
}) => {
  const { cx, cy, payload } = props;
  const value = payload?.emotionalWellness || 0;

  const getEmoji = (score: number) => {
    switch (score) {
      case 1:
        return "😢";
      case 2:
        return "😟";
      case 3:
        return "😐";
      case 4:
        return "😊";
      case 5:
        return "😁";
      default:
        return "😐";
    }
  };

  if (!value) return null;

  return (
    <text
      x={cx}
      y={cy}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize="20"
    >
      {getEmoji(value)}
    </text>
  );
};

export const EmotionalWellbeingChart: React.FC<
  EmotionalWellbeingChartProps
> = ({ data, className }) => {
  const { language, t } = useI18nStore();

  // ✅ FHIR Compliance: Sort by timestamp for chronological accuracy
  const sortedData = [...data].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  // Transform data with localized timestamps and normalize field names
  // ✅ Filter out entries with emotionalWellbeing = 0 to prevent lines going through zero
  const chartData = sortedData
    .filter(
      (item) => (item.emotionalWellbeing || item.emotionalWellness || 0) > 0,
    )
    .map((item) => ({
      ...item,
      emotionalWellness: item.emotionalWellbeing || item.emotionalWellness || 0,
      fhirTimestamp: formatTooltipTimestamp(item.timestamp, language),
      // Always format in the app language: the service's formattedDate may
      // come from the backend in a fixed locale.
      formattedDate: item.timestamp
        ? formatChartDate(item.timestamp, language)
        : item.formattedDate,
    }));

  // Check if we have any wellness data
  const hasEmotionalData = chartData.some((d) => d.emotionalWellness > 0);

  if (!hasEmotionalData || chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
        <div className="text-center text-gray-500">
          <p className="mb-2">📊 {t.noWellbeingData}</p>
          <p className="text-sm">{t.saveFirstWellbeingEntry}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <ResponsiveContainer
        width="100%"
        height={320}
        data-testid="responsive-container"
      >
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 8, left: 0, bottom: 0 }}
          data-testid="line-chart"
        >
          <CartesianGrid strokeDasharray="3 3" data-testid="cartesian-grid" />
          <XAxis
            dataKey="formattedDate"
            tick={{ fontSize: 12 }}
            data-testid="x-axis"
          />
          <YAxis
            domain={[0, 5]}
            ticks={[0, 1, 2, 3, 4, 5]}
            tick={{ fontSize: 12 }}
            data-testid="y-axis"
          />
          <Tooltip content={<CustomTooltip />} data-testid="tooltip" />
          <Legend data-testid="legend" />

          {/* Reference Lines for Wellbeing Thresholds */}
          <ReferenceLine
            y={4}
            stroke="#10b981"
            strokeDasharray="3 3"
            label={{
              value: `${t.good} (4/5)`,
              position: "right",
              fontSize: 11,
            }}
            data-testid="reference-line-good"
            data-y="4"
          />
          <ReferenceLine
            y={3}
            stroke="#f59e0b"
            strokeDasharray="3 3"
            label={{
              value: `${t.average} (3/5)`,
              position: "right",
              fontSize: 11,
            }}
            data-testid="reference-line-average"
            data-y="3"
          />
          <ReferenceLine
            y={2}
            stroke="#ef4444"
            strokeDasharray="3 3"
            label={{ value: `${t.low} (2/5)`, position: "right", fontSize: 11 }}
            data-testid="reference-line-low"
            data-y="2"
          />

          {/* Emotional Wellbeing Line */}
          <Line
            type="monotone"
            dataKey="emotionalWellness"
            stroke="#a855f7"
            strokeWidth={2}
            dot={<CustomDot />}
            name={`😊 ${t.emotionalWellbeing}`}
            data-testid="line"
            data-name={`😊 ${t.emotionalWellbeing}`}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Chart Summary - FHIR Data Points */}
      <div className="mt-4 flex justify-between items-center text-sm text-gray-600 px-2">
        <div className="flex items-center space-x-4">
          <span>
            📊 {chartData.length} {t.wellnessDataPoints} (365 {t.days})
          </span>
          {chartData.length > 0 && chartData[chartData.length - 1] && (
            <>
              {(chartData[chartData.length - 1]?.emotionalWellness || 0) >
                0 && (
                <span>
                  😊 {t.emotionalWellbeing}:{" "}
                  {chartData[chartData.length - 1]?.emotionalWellness}/5
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
